import { applySnapshot, flow, Instance, types } from 'mobx-state-tree';
import axios from 'axios';
import moment from 'moment';
import ObjectPath from 'object-path';
import { TokenResponse } from '@kushki/js/lib/types/remote/token_response';
import { ErrorResponse } from '@kushki/js/lib/types/error_response';
import safeJsonStringify from 'safe-json-stringify';

import { isCardNumberValid, isCVVNumberValid } from '@utils/validators';
import { kushkiClientBuilder } from '@utils/kushkiClient';
import endpoints from '@configs/endpoints';
import { isProd, kushkiIdentification } from '@configs/variables';
import Common from '@store/common';
import { getCookie } from '@utils/cookies';
import getCardInfo from '@utils/getCardInfo';
import {
  IKushkiCashPaymentData,
  IKushkiPaymentData,
} from '@containers/checkout/payment-form/types';
import { Currencies } from '@store/checkout';
import { Partner } from '@constants/paymentLink';
import sendPaymentError from '@utils/sendPaymentError';
import { toJS } from 'mobx';
import { sleep } from '@utils/sleep';

export type otpKushkiCodes = {
  [key: string]: string;
};

interface IError {
  code: string;
}

const Security3ds = types.model({
  acsURL: types.optional(types.string, ''),
  authenticationTransactionId: types.optional(types.string, ''),
  paReq: types.optional(types.string, ''),
  specificationVersion: types.optional(types.string, ''),
  authRequired: types.optional(types.boolean, false),
});

const Card = types
  .compose(
    Common,
    types.model({
      name: types.optional(types.string, ''),
      number: types.optional(types.string, ''),
      expiryMonth: types.optional(types.string, ''),
      expiryYear: types.optional(types.string, ''),
      cvv: types.optional(types.string, ''),
      zipCode: types.optional(types.string, ''),
      token: types.optional(types.string, ''),
      secureId: types.optional(types.string, ''),
      code: types.optional(types.string, ''),
      secureService: types.optional(types.string, ''),
      security: types.optional(Security3ds, {}),
    }),
  )
  .actions((self) => ({
    preauthorizeFromKushki: flow(function* (data: IKushkiPaymentData, merchantId: string) {
      const { kushki } = self.globalStore;
      const { bin, last4 } = getCardInfo(data.cardNumber);
      try {
        kushki.updateField('flags.requestingPreauthorization', true);
        yield new Promise((resolve, reject) => {
          kushkiClientBuilder({
            merchantId,
          }).requestSecureServiceValidation(
            {
              secureServiceId: self.secureId,
              otpValue: self.code,
            },
            (response: { isValid?: boolean; code?: string; message?: string }) => {
              if (response.code === 'OTP000') {
                return resolve(response);
              }

              reject(response);
            },
          );
        });
        kushki.initCardTransaction(self.token, data);
        kushki.updateField('flags.isEnteringCodeForPreauth', false);
      } catch (err) {
        const errorJsonStringify = err as Record<string, unknown>;
        const error = err as IError;

        if (error.code === 'OTP200') {
          kushki.updateField('flags.isEnteringCodeForPreauth', false);
        }

        sendPaymentError({
          ...data,
          partner: Partner.KUSHKI,
          paymentMethod: self.globalStore.checkout.paymentMethod,
          error: safeJsonStringify(errorJsonStringify),
          cardInfo: {
            holderName: data.cardHolderName,
            bin,
            last4,
          },
        });

        throw new Error(error.code);
      } finally {
        kushki.updateField('flags.requestingPreauthorization', false);
      }
    }),
    requestValidate3DS: flow(function* (data: IKushkiPaymentData, merchantId: string) {
      const { kushki } = self.globalStore;
      const { bin, last4 } = getCardInfo(data.cardNumber);

      try {
        kushki.updateField('flags.requestValidate3DS', true);
        yield new Promise((resolve, reject) => {
          kushkiClientBuilder({
            merchantId,
          }).requestValidate3DS(
            {
              secureId: self.secureId,
              security: toJS(self.security),
            },
            (response: { isValid?: boolean; code?: string; message?: string }) => {
              if (response.code === '3DS000' || response.isValid) {
                kushki.initCardTransaction(self.token, data);
                return resolve(response);
              }

              reject(response);
            },
          );
        });
      } catch (err) {
        const errorJsonStringify = err as Record<string, unknown>;
        const error = err as IError;

        sendPaymentError({
          ...data,
          partner: Partner.KUSHKI,
          paymentMethod: self.globalStore.checkout.paymentMethod,
          error: safeJsonStringify(errorJsonStringify),
          cardInfo: {
            holderName: data.cardHolderName,
            bin,
            last4,
          },
        });

        throw new Error(error.code);
      } finally {
        kushki.updateField('flags.requestValidate3DS', false);
        // kushki.updateField('flags.is3DSPreauth', false);
      }
    }),
  }));

const CardErrors = types.model({
  name: types.optional(types.boolean, false),
  number: types.optional(types.boolean, false),
  expiryMonth: types.optional(types.boolean, false),
  expiryYear: types.optional(types.boolean, false),
  cvv: types.optional(types.boolean, false),
  zipCode: types.optional(types.boolean, false),
  message: types.optional(types.string, ''),
});

const KushkiStore = types
  .compose(
    Common,
    types.model({
      cashReferenceNumber: types.optional(types.string, ''),
      barcodeUrl: '',
      card: types.optional(Card, {}),
      flags: types.optional(
        types.model({
          requestingToken: types.optional(types.boolean, false),
          requestingProcessCardPayment: types.optional(types.boolean, false),
          requestingCashPaymentReference: types.optional(types.boolean, false),
          isEnteringCodeForPreauth: types.optional(types.boolean, false),
          requestingPreauthorization: types.optional(types.boolean, false),
          is3DSPreauth: types.optional(types.boolean, false),
          requestValidate3DS: types.optional(types.boolean, false),
        }),
        {},
      ),
      errors: types.optional(
        types.model({
          card: types.optional(CardErrors, {}),
          process: types.optional(
            types.model({
              cardPayment: types.optional(types.boolean, false),
              cashPayment: types.optional(types.boolean, false),
            }),
            {},
          ),
        }),
        {},
      ),
      success: types.optional(
        types.model({
          cardPayment: types.optional(types.boolean, false),
          cashPayment: types.optional(types.boolean, false),
        }),
        {},
      ),
    }),
  )
  .views((self) => ({
    get cardNumber() {
      return self.card.number.replace(/\s/g, '');
    },
    get expirationDate() {
      return `${self.card.expiryMonth}/${self.card.expiryYear}`;
    },
    get hasCardErrors() {
      return Object.values(self.errors.card).some((value) => value === true);
    },
    get hasCashErrors() {
      // TODO: implement cash validation if is needed
      return false;
    },
    get hasCardProcessErrors() {
      return self.errors.process.cardPayment;
    },
    get hasCashProcessErrors() {
      return self.errors.process.cashPayment;
    },
    get isProcessing() {
      return (
        self.flags.requestingToken ||
        self.flags.requestingProcessCardPayment ||
        self.flags.requestValidate3DS ||
        self.flags.requestingCashPaymentReference
      );
    },
  }))
  .actions((self) => ({
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    updateField: (field: string, value: any) => {
      ObjectPath.set(self, field, value);
      applySnapshot(self, self);
    },
    updateExpirationDate: (date: string) => {
      const [month, year] = date.split('/');
      self.card.expiryMonth = month;
      self.card.expiryYear = year;
    },
    resetCardData: () => {
      self.card.name = '';
      self.card.number = '';
      self.card.expiryMonth = '';
      self.card.expiryYear = '';
      self.card.cvv = '';
      self.card.zipCode = '';
      self.card.code = '';
      self.card.secureId = '';
      self.card.secureService = '';
    },
    resetCardValidations: () => {
      self.errors.card.name = false;
      self.errors.card.number = false;
      self.errors.card.expiryMonth = false;
      self.errors.card.expiryYear = false;
      self.errors.card.cvv = false;
      self.errors.card.zipCode = false;
      self.errors.card.message = '';
    },
    resetProcessErrors: () => {
      self.errors.process.cardPayment = false;
      self.errors.process.cashPayment = false;
    },
    resetFlags: () => {
      self.flags.requestingToken = false;
      self.flags.requestingProcessCardPayment = false;
      self.flags.requestingCashPaymentReference = false;
    },
    resetSuccessStatus: () => {
      self.success.cardPayment = false;
      self.success.cashPayment = false;
    },
  }))
  .actions((self) => ({
    resetState: () => {
      self.resetCardData();
      self.resetCardValidations();
      self.resetFlags();
      self.resetSuccessStatus();
    },
    resetTransactionState: () => {
      self.resetFlags();
      self.resetCardValidations();
      // TODO: reset cash validation if is needed
      self.resetProcessErrors();
      self.resetSuccessStatus();
    },
  }))
  .actions((self) => ({
    validateCardData: () => {
      const now = moment();
      const currentMonth = +now.format('MM');
      const currentYear = +now.format('YY');
      const month = +self.card.expiryMonth;
      const year = +self.card.expiryYear;
      const stringMonth = self.card.expiryMonth;
      const stringYear = self.card.expiryYear;
      const expiryDate = moment(`${stringMonth}/${stringYear}`, 'MM/YY');
      const referenceDate = moment(`${now.format('MM')}/${now.format('YY')}`, 'MM/YY');
      const isValidMonth = month >= 1 && month <= 12 && month >= currentMonth;
      self.errors.card.name = !(self.card.name.length >= 1);
      self.errors.card.number = !isCardNumberValid(self.card.number);
      self.errors.card.cvv = !isCVVNumberValid(self.card.cvv);
      self.errors.card.expiryMonth =
        self.card.expiryYear.length === 2
          ? !expiryDate.isSameOrAfter(referenceDate)
          : !isValidMonth;
      self.errors.card.expiryYear = !(year >= currentYear);
      /*self.errors.card.zipCode =
        self.isZipCodeRequired &&
        !isZipCodeValid(self.card.zipCode, self.locale);*/
      return !self.hasCardErrors;
    },
  }))
  .actions((self) => ({
    getCardToken(
      amount: number,
      data: IKushkiPaymentData,
      callback: (
        response: TokenResponse | ErrorResponse,
        kushkiPaymentData: IKushkiPaymentData,
      ) => void,
      merchantId: string,
    ) {
      if (!self.flags.requestingToken) {
        const { links } = self.globalStore;
        const { paymentLink } = links;
        const { isSubscription } = paymentLink;
        const { bin, last4 } = getCardInfo(data.cardNumber);
        try {
          self.flags.requestingToken = true;
          const [month, year] = data.cardExpiration.split('/');

          if (isSubscription) {
            kushkiClientBuilder({
              merchantId,
            }).requestSubscriptionToken(
              {
                currency: Currencies.mxn,
                card: {
                  name: data.cardHolderName,
                  number: data.cardNumber,
                  cvc: data.cardCvv,
                  expiryMonth: month,
                  expiryYear: year,
                },
              },
              (response) => {
                // Update state with action because is not possible update directly in the callback
                self.updateField('flags.requestingToken', false);
                const resp = response as ErrorResponse;
                if (resp.error) {
                  self.updateField('errors.card.message', resp.message);
                  return self.updateField('errors.process.cardPayment', true);
                }
                callback(resp, data);
              },
            );
          } else {
            kushkiClientBuilder({
              merchantId,
            }).requestToken(
              {
                amount: `${amount}`,
                currency: Currencies.mxn,
                card: {
                  name: data.cardHolderName,
                  number: data.cardNumber,
                  cvc: data.cardCvv,
                  expiryMonth: month,
                  expiryYear: year,
                },
              },
              (response) => {
                // Update state with action because is not possible update directly in the callback
                self.updateField('flags.requestingToken', false);
                const resp = response as ErrorResponse;
                if (resp.error) {
                  sendPaymentError({
                    ...data,
                    partner: Partner.KUSHKI,
                    paymentMethod: self.globalStore.checkout.paymentMethod,
                    error: safeJsonStringify(resp),
                    cardInfo: {
                      holderName: data.cardHolderName,
                      bin,
                      last4,
                    },
                  });

                  self.updateField('errors.card.message', resp.message);
                  return self.updateField('errors.process.cardPayment', true);
                }
                callback(resp, data);
              },
            );
          }
        } catch (e) {
          const error = e as Record<string, unknown>;
          sendPaymentError({
            ...data,
            partner: Partner.KUSHKI,
            paymentMethod: self.globalStore.checkout.paymentMethod,
            error: safeJsonStringify(error),
          });

          self.flags.requestingToken = false;
          self.errors.process.cardPayment = true;
        }
      }
    },
    initCardTransaction: flow(function* (token: string, payload: IKushkiPaymentData) {
      if (!self.flags.requestingProcessCardPayment) {
        try {
          self.flags.requestingProcessCardPayment = true;
          const { bin, last4 } = getCardInfo(payload.cardNumber);

          const addressDetails = {
            address: payload.payerAddress,
            city: payload.payerCity,
            region: payload.payerState,
            country: payload.payerCountry,
            zipCode: payload.payerZipCode,
          };

          const body = {
            token,
            paymentLinkId: payload.paymentLinkId,
            payeeFirstName: payload.payeeFirstName,
            payeeLastName: payload.payeeLastName,
            payeeEmail: payload.payeeEmail.trim(),
            payeePhone: payload.payeePhone.replace(/\s/g, ''),
            payeeZipCode: payload.zipCode,
            partner: Partner.KUSHKI,
            addressDetails,
            cardInfo: {
              holderName: payload.cardHolderName,
              bin,
              last4,
            },
          };

          const { links } = self.globalStore;
          const { paymentLink } = links;
          const { isSubscription } = paymentLink;

          const { data } = yield axios.post(
            !isSubscription ? endpoints.processCardPayment : endpoints.processCardSubscription,
            body,
          );
          self.flags.requestingProcessCardPayment = false;
          if (data.error) {
            self.errors.process.cardPayment = true;
            self.updateField(
              'errors.card.message',
              `${data.errors.message} ${data?.errors?.details || ''}`,
            );
            throw new Error(data.msg);
          }
          self.resetCardData();
          self.success.cardPayment = true;
          self.globalStore.checkout.setCardPaymentSuccess();
        } catch (e: any) {
          self.flags.requestingProcessCardPayment = false;
          self.errors.process.cardPayment = true;
          self.updateField(
            'errors.card.message',
            `${e.errors.message} ${e?.errors?.details || ''}`,
          );
        }
      }
    }),
    getCashToken(
      data: IKushkiCashPaymentData,
      callback: (response: TokenResponse | ErrorResponse) => void,
      merchantId: string,
    ) {
      if (self.flags.requestingToken) return;

      try {
        self.flags.requestingToken = true;
        kushkiClientBuilder({
          merchantId,
        }).requestCashToken(
          {
            name: data.payeeFirstName,
            lastName: data.payeeLastName,
            totalAmount: +data.amount,
            currency: Currencies.mxn,
            identification: isProd ? '1000000000' : getCookie(kushkiIdentification, '1234567890'),
          },
          (response: TokenResponse | ErrorResponse) => {
            self.updateField('flags.requestingToken', false);
            if ((response as ErrorResponse).error) {
              sendPaymentError({
                ...data,
                partner: Partner.KUSHKI,
                paymentMethod: self.globalStore.checkout.paymentMethod,
                error: safeJsonStringify(response),
              });
              return self.updateField('errors.process.cashPayment', true);
            }
            callback(response);
          },
        );
      } catch (e) {
        const error = e as Record<string, unknown>;
        sendPaymentError({
          ...data,
          partner: Partner.KUSHKI,
          paymentMethod: self.globalStore.checkout.paymentMethod,
          error: safeJsonStringify(error),
        });
        self.flags.requestingToken = false;
        self.errors.process.cashPayment = true;
      }
    },
    initCashTransaction: flow(function* (token: string, payload: IKushkiCashPaymentData) {
      if (self.flags.requestingCashPaymentReference) return;

      try {
        self.flags.requestingCashPaymentReference = true;
        const body = {
          token,
          paymentLinkId: payload.paymentLinkId,
          payeeFirstName: payload.payeeFirstName,
          payeeLastName: payload.payeeLastName,
          payeeEmail: payload.payeeEmail.trim(),
          payeePhone: payload.payeePhone.replace(/\s/g, ''),
        };
        const { data } = yield axios.post(endpoints.processCashPayment, body);
        if (data.error) throw new Error(data.msg);
        self.globalStore.checkout.success.set('cashPayment', true);
        self.cashReferenceNumber = data.cashPaymentReference.referenceNumber;
        self.barcodeUrl = data.cashPaymentReference.barcodeUrl;
      } catch (e) {
        self.errors.process.cashPayment = true;
      } finally {
        self.flags.requestingCashPaymentReference = false;
      }
    }),
  }))
  .actions((self) => ({
    processCardPayment: (data: IKushkiPaymentData, merchantId: string) => {
      if (!self.flags.requestingToken && !self.flags.requestingProcessCardPayment) {
        self.resetTransactionState();
        try {
          self.getCardToken(
            +data.amount,
            data,
            async (response) => {
              const { token, secureId, secureService, security } = response as TokenResponse;

              if (!secureId) {
                await self.initCardTransaction(token, data);
                return;
              }

              self.card.updateField('token', token);
              self.card.updateField('secureId', secureId);
              self.card.updateField('secureService', secureService);

              // OTP FLOW
              if (secureService === 'KushkiOTP') {
                console.log('OTP FLOW');
                self.updateField('flags.isEnteringCodeForPreauth', true);
              }

              // 3DS FLOW
              if (secureService === '3dsecure') {
                console.log('3DS FLOW');
                if (security?.authRequired) {
                  self.updateField('flags.is3DSPreauth', true);
                  await sleep(5000);
                  self.updateField('flags.is3DSPreauth', false);
                }
                self.card.updateField('security', security);
                self.card.requestValidate3DS(data, merchantId);
              }
            },
            merchantId,
          );
        } catch (e) {
          self.flags.requestingToken = false;
          self.flags.requestingProcessCardPayment = false;
          self.errors.process.cardPayment = true;
        }
      }
    },
    generateCashReference: (data: IKushkiCashPaymentData, merchantId: string) => {
      if (self.flags.requestingToken && self.flags.requestingCashPaymentReference) return;

      try {
        self.resetTransactionState();
        self.getCashToken(
          data,
          async (response: TokenResponse | ErrorResponse) => {
            const { token } = response as TokenResponse;
            await self.initCashTransaction(token, data);
          },
          merchantId,
        );
      } catch (e) {
        const error = e as Record<string, unknown>;
        sendPaymentError({
          ...data,
          partner: Partner.KUSHKI,
          paymentMethod: self.globalStore.checkout.paymentMethod,
          error: safeJsonStringify(error),
        });
        self.flags.requestingToken = false;
        self.flags.requestingCashPaymentReference = false;
        self.errors.process.cashPayment = true;
      }
    },
  }));

export type IKushkiStore = Instance<typeof KushkiStore>;
export default KushkiStore;
